import React, { useState, useEffect, useCallback } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ItemCard from './ItemCard';

// Stable empty-object reference so the styling prop passed to memoized
// ItemCards doesn't change identity on every container render.
const EMPTY_OBJECT = {};

/**
 * Item Card Container with Location Grouping and Drag & Drop
 * 
 * Features:
 * - Groups items by location
 * - Provides drag and drop context
 * - Handles item reordering within locations
 * - Supports sorting options
 * - Configurable display options
 * 
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of item objects
 * @param {Function} props.onMove - Move handler for drag and drop
 * @param {Function} props.onRemove - Remove item handler
 * @param {Function} props.onUpdatePar - PAR level update handler
 * @param {Function} props.onUpdateMOQ - MOQ update handler
 * @param {Function} props.onItemClick - Item click handler
 * @param {Object} props.config - Configuration options
 * @param {boolean} props.config.groupByLocation - Group items by location (default: true)
 * @param {boolean} props.config.showLocationHeaders - Show location headers (default: true)
 * @param {string} props.config.sortBy - Sort items by field ('position', 'name', 'location')
 * @param {boolean} props.config.sortAscending - Sort direction (default: true)
 * @param {Object} props.cardConfig - Configuration passed to ItemCard components
 * @param {Object} props.styling - Custom styling options
 */
const ItemCardContainer = ({
  items = [],
  onMove,
  onRemove,
  onUpdatePar,
  onUpdateMOQ,
  onItemClick,
  selectedItemIds = new Set(),
  onToggleItemSelect,
  selectable = false,
  config = {},
  cardConfig = {},
  styling = {}
}) => {
  const [groupedItems, setGroupedItems] = useState({});

  // Configuration with defaults
  const {
    groupByLocation = true,
    showLocationHeaders = true,
    sortBy = 'position',
    sortAscending = true
  } = config;

  // Group items by location
  const groupByLocationFn = (items) => {
    if (!groupByLocation) {
      return { 'All Items': items };
    }

    const grouped = {};
    items.forEach(item => {
      const location = item.location || item.item_snapshot?.location || 'Unassigned';
      if (!grouped[location]) {
        grouped[location] = [];
      }
      grouped[location].push(item);
    });

    return grouped;
  };

  // Sort items within each group
  const sortItems = (items, sortBy, ascending = true) => {
    const sorted = [...items].sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'position':
          aVal = a.count_position || 999;
          bVal = b.count_position || 999;
          break;
        case 'name':
          aVal = (a.description || a.name || '').toLowerCase();
          bVal = (b.description || b.name || '').toLowerCase();
          break;
        case 'location':
          aVal = (a.location || '').toLowerCase();
          bVal = (b.location || '').toLowerCase();
          break;
        case 'vendor':
          aVal = (a.vendor || '').toLowerCase();
          bVal = (b.vendor || '').toLowerCase();
          break;
        case 'par':
          aVal = a.par_level || 0;
          bVal = b.par_level || 0;
          break;
        case 'moq':
          aVal = a.min_order_qty || a.moq || 0;
          bVal = b.min_order_qty || b.moq || 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return ascending ? -1 : 1;
      if (aVal > bVal) return ascending ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  // Update grouped items when items or config changes
  useEffect(() => {
    const grouped = groupByLocationFn(items);
    
    // Sort items within each group
    Object.keys(grouped).forEach(location => {
      grouped[location] = sortItems(grouped[location], sortBy, sortAscending);
    });

    setGroupedItems(grouped);
  }, [items, groupByLocation, sortBy, sortAscending]);

  // Handle move within location. useCallback keeps the reference stable so it
  // doesn't defeat ItemCard's memoization.
  const handleMove = useCallback((location, fromIndex, toIndex) => {
    if (onMove) {
      onMove(location, fromIndex, toIndex);
    }

    // Update local state for immediate feedback
    setGroupedItems(prev => {
      const newGrouped = { ...prev };
      const locationItems = [...newGrouped[location]];
      const [movedItem] = locationItems.splice(fromIndex, 1);
      locationItems.splice(toIndex, 0, movedItem);
      
      // Update positions
      const updatedItems = locationItems.map((item, index) => ({
        ...item,
        count_position: index + 1
      }));
      
      newGrouped[location] = updatedItems;
      return newGrouped;
    });
  }, [onMove]);

  // Default styling
  const containerStyle = {
    ...styling.container
  };

  // Stable reference for per-card styling so memoized cards aren't forced to
  // re-render just because the container re-rendered.
  const cardStyling = styling.card || EMPTY_OBJECT;

  const locationHeaderStyle = {
    color: '#5A4A42',
    fontSize: '0.9rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    paddingBottom: '0.25rem',
    borderBottom: '1px solid #dee2e6',
    ...styling.locationHeader
  };

  const locationGroupStyle = {
    marginBottom: '1.5rem',
    ...styling.locationGroup
  };

  if (!items.length) {
    return (
      <div className="text-muted text-center py-4" style={styling.emptyState}>
        No items to display
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div style={containerStyle}>
        {Object.entries(groupedItems).map(([location, locationItems]) => (
          <div key={location} style={locationGroupStyle}>
            {/* Location Header */}
            {showLocationHeaders && groupByLocation && (
              <div style={locationHeaderStyle}>
                <i className="feather icon-map-pin me-2"></i>
                {location} ({locationItems.length} items)
              </div>
            )}

            {/* Items */}
            <div>
              {locationItems.map((item, index) => (
                <ItemCard
                  key={`${item.id}-${location}`}
                  item={item}
                  index={index}
                  location={location}
                  onMove={handleMove}
                  onRemove={onRemove}
                  onUpdatePar={onUpdatePar}
                  onUpdateMOQ={onUpdateMOQ}
                  onClick={onItemClick}
                  config={cardConfig}
                  selectable={selectable}
                  selected={
                    selectedItemIds instanceof Set
                      ? selectedItemIds.has(item.id)
                      : (Array.isArray(selectedItemIds) ? selectedItemIds.includes(item.id) : false)
                  }
                  onToggleSelect={onToggleItemSelect}
                  styling={cardStyling}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </DndProvider>
  );
};

export default ItemCardContainer;
